import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { getApiKeyResourceList } from "../../../../Api/ClientApiManagementApi";
import CODE from "../../../../Static/Constants/StatusCodes";
import { SetSessionExpired } from "../../../../Actions";

const ROWS_PER_PAGE = 10;

const normalizeRow = (row = {}) => {
  const depot = row.originDepo ?? row.originDepot ?? null;
  return {
    ...row,
    originDepo: depot,
    originDepot: depot,
  };
};

const filterRows = (rows, filter) => {
  if (!filter.column || filter.devices.length === 0) return rows;

  return rows.filter((row) =>
    filter.devices.some((value) =>
      String(row?.[filter.column] ?? "")
        .toLowerCase()
        .includes(value.toLowerCase())
    )
  );
};

const ALWAYS_REQUESTED_COLUMNS = ["pingStatus"];

const buildRequestedColumns = (tableConfig) => {
  const { columnOrdering = [], columnsConfig = {} } = tableConfig || {};
  const requestedColumns = [];

  columnOrdering.forEach((columnKey) => {
    const config = columnsConfig[columnKey];
    const key = config?.key || columnKey;

    if (!config || requestedColumns.includes(key)) return;
    requestedColumns.push(key);
  });

  // Only append when real columns exist — keeps the length===0 guard intact
  // so we don't fire a premature request before tableConfig metadata has loaded.
  if (requestedColumns.length > 0) {
    ALWAYS_REQUESTED_COLUMNS.forEach((col) => {
      if (!requestedColumns.includes(col)) requestedColumns.push(col);
    });
  }

  return requestedColumns;
};

export const useAccessGrantedEntitiesData = (apiKeyId, tableConfig, appliedFilters = {}) => {
  const dispatch = useDispatch();

  const searchFilterRef = useRef({ devices: [], column: null });
  const filteredRowsRef = useRef(null);
  const allRowsCacheRef = useRef(null);
  // Fix 1: generation counter to discard stale applySearch results
  const searchGenRef = useRef(0);
  // Fix 2: generation counter to abort in-flight fetchAllRows after apiKeyId/config change
  const fetchGenRef = useRef(0);

  const [pageData, setPageData] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const requestedColumns = buildRequestedColumns(tableConfig);
  const requestedColumnsKey = requestedColumns.join("|");
  const appliedFiltersKey = JSON.stringify(appliedFilters);

  useEffect(() => {
    if (!apiKeyId || requestedColumns.length === 0) {
      setPageData([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }

    searchFilterRef.current = { devices: [], column: null };
    filteredRowsRef.current = null;
    allRowsCacheRef.current = null;
    // Fix 2: bump generation so any in-flight fetchAllRows for the old key aborts
    fetchGenRef.current += 1;
    setPageNumber(1);
    loadPageDetails(1);
  }, [apiKeyId, requestedColumnsKey, appliedFiltersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchResourcePage = async (pageNum, pageCount = ROWS_PER_PAGE) => {
    const res = await getApiKeyResourceList(apiKeyId, {
      columns: requestedColumns,
      pageNum,
      pageCount,
      filters: appliedFilters,
    });

    if (res?.responseStatus?.code === CODE.SESSION_EXPIRED) {
      dispatch(SetSessionExpired(true));
      return null;
    }

    if (res?.responseStatus?.code !== CODE.SUCCESS) {
      return { rows: [], totalCount: 0 };
    }

    return {
      rows: (res.response?.data || []).map(normalizeRow),
      totalCount: res.response?.total?.total || 0,
    };
  };

  const loadPageDetails = async (pageNum) => {
    setIsLoading(true);
    const result = await fetchResourcePage(pageNum);
    setPageData(result?.rows || []);
    setTotal(result?.totalCount || 0);
    setIsLoading(false);
  };

  const fetchAllRows = async () => {
    if (allRowsCacheRef.current !== null) return allRowsCacheRef.current;

    // Fix 2: snapshot generation before the first await
    const gen = fetchGenRef.current;

    const firstPage = await fetchResourcePage(1, ROWS_PER_PAGE);
    // Fix 2: abort if apiKeyId/config changed while we were awaiting
    if (gen !== fetchGenRef.current) return [];
    if (!firstPage) return [];

    const totalCount = firstPage.totalCount;
    let rows = [...firstPage.rows];
    let nextPage = 2;

    while (rows.length < totalCount) {
      const pageResult = await fetchResourcePage(nextPage, ROWS_PER_PAGE);
      // Fix 2: abort mid-pagination if key/config changed
      if (gen !== fetchGenRef.current) return [];
      if (!pageResult) return rows;
      rows = rows.concat(pageResult.rows);
      // Fix 3: empty page means error/early termination — do NOT cache partial data
      if (pageResult.rows.length === 0) break;
      nextPage += 1;
    }

    // Fix 3: only cache when fetch is complete; also re-check generation before writing
    if (gen === fetchGenRef.current && rows.length >= totalCount) {
      allRowsCacheRef.current = rows;
    }
    return rows;
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
    if (filteredRowsRef.current !== null) {
      setPageData(
        filteredRowsRef.current.slice(
          (newPage - 1) * ROWS_PER_PAGE,
          newPage * ROWS_PER_PAGE
        )
      );
    } else {
      loadPageDetails(newPage);
    }
  };

  const applySearch = async (devices, column) => {
    const newFilter = { devices, column };
    searchFilterRef.current = newFilter;
    // Fix 1: bump search generation so any in-flight search is discarded
    const gen = ++searchGenRef.current;
    setPageNumber(1);

    if (devices.length === 0 || !column) {
      filteredRowsRef.current = null;
      // Fix 4: invalidate row cache on search clear so next search re-fetches fresh data
      allRowsCacheRef.current = null;
      loadPageDetails(1);
      return;
    }

    setIsLoading(true);
    const allRows = await fetchAllRows();
    // Fix 1: discard result if a newer search was started while we were awaiting
    if (gen !== searchGenRef.current) return;
    const filteredRows = filterRows(allRows, newFilter);
    filteredRowsRef.current = filteredRows;
    setTotal(filteredRows.length);
    setPageData(filteredRows.slice(0, ROWS_PER_PAGE));
    setIsLoading(false);
  };

  const getAllRows = async () => {
    if (filteredRowsRef.current !== null) return filteredRowsRef.current;
    return fetchAllRows();
  };

  return {
    pagedData: pageData,
    total,
    isLoading,
    pageNumber,
    setPageNumber: handlePageChange,
    applySearch,
    getAllRows,
  };
};
